import { Link } from 'react-router-dom';
import { useGlobal } from '@/context/GlobalContext';
import { MapPin, Mail, Phone, MessageCircle, ExternalLink, Linkedin, Facebook } from 'lucide-react';

const Footer = () => {
  const { settings } = useGlobal();

  return (
    <footer className="bg-[#141824]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src={settings.logo_url} 
                alt="Anchilex" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-4" style={{ fontFamily: "'Exo 2', sans-serif" }}>
              Clear Vision • Every Journey • Every Space
            </p>
            <p className="text-slate-500 text-xs" style={{ fontFamily: "'Exo 2', sans-serif" }}>
              Engineering & solutions partner for imaging and safety systems since 1995.
            </p>
            {/* Social Links */}
            {(settings.linkedin_url || settings.facebook_url) && (
              <div className="flex gap-3 mt-4">
                {settings.linkedin_url && (
                  <a 
                    href={settings.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-[#232a3b] border border-[#3d4758] flex items-center justify-center text-slate-400 hover:text-white hover:border-[#5b3589] transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
                {settings.facebook_url && (
                  <a 
                    href={settings.facebook_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-[#232a3b] border border-[#3d4758] flex items-center justify-center text-slate-400 hover:text-white hover:border-[#5b3589] transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Solutions</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/mobility-imaging" className="text-slate-400 hover:text-white text-sm transition-colors">
                  Mobility Imaging
                </Link>
                <p className="text-slate-600 text-xs mt-0.5">Dashcams, MDVRs, e-Mirrors</p>
              </li>
              <li>
                <Link to="/surveillance" className="text-slate-400 hover:text-white text-sm transition-colors">
                  Surveillance Systems
                </Link>
                <p className="text-slate-600 text-xs mt-0.5">IP/Analog, NVRs, NDAA-compliant</p>
              </li>
              <li>
                <Link to="/ultrasonic" className="text-slate-400 hover:text-white text-sm transition-colors">
                  Ultrasonic Deterrence
                </Link>
                <p className="text-slate-600 text-xs mt-0.5">Bird & animal control systems</p>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/engineering" className="text-slate-400 hover:text-white text-sm transition-colors">
                  OEM/ODM Services
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <a 
                  href="https://www.anchilex.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
                >
                  Anchilex.com
                  <ExternalLink className="w-3 h-3" />
                </a>
                <p className="text-slate-600 text-xs mt-0.5">Motorcycle dashcams</p>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Contact</h4>
            {settings.company_name && (
              <p className="text-slate-300 text-sm font-medium mb-4">
                {settings.company_name}
              </p>
            )}
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#8b5cf6] mt-0.5 shrink-0" />
                <span className="text-slate-400 text-sm">
                  15F.-10, No. 99, Sec. 1, Xintai 5th Rd.,<br />
                  Xizhi Dist., New Taipei City 22175, Taiwan
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#8b5cf6] mt-0.5 shrink-0" />
                <a href="mailto:contact@anchilex.com" className="text-slate-400 hover:text-white text-sm transition-colors">
                  contact@anchilex.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#8b5cf6] mt-0.5 shrink-0" />
                <a href="tel:+886226971626" className="text-slate-400 hover:text-white text-sm transition-colors">
                  +886 2 2697-1626
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 text-[#8b5cf6] mt-0.5 shrink-0" />
                <a href="https://wa.me/886901028937" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white text-sm transition-colors">
                  +886 901 028 937
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Gradient Line */}
        <div className="mt-12 h-px bg-gradient-to-r from-transparent via-[#5b3589] to-transparent" />

        {/* Bottom Row */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Trinity Electronics Co., LTD (Anchilex). All rights reserved.
          </p>
          <p className="text-slate-600 text-xs">
            Engineering support in EN, 中文, DE, FR, RU, SV, JP
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
